import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localeNlExtra from '@angular/common/locales/extra/nl';
import locales from '@angular/common/locales/nl';
import { APP_INITIALIZER, ErrorHandler, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { SplashScreen } from '@capacitor/splash-screen';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { ResourceActionReturnType, ResourceGlobalConfig, ResourceQueryMappingMethod } from '@ngx-resource/core';
import { ResourceModule } from '@ngx-resource/handler-ngx-http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from "@sentry/angular-ivy";
import { environment } from '../environments/environment';
import { ApiModule } from './api/api.module';
import { AuthGuard } from './api/authGuard.service';
import { AuthInterceptor } from './api/authInterceptor.service';
import { LoginGuard } from './api/loginGuard.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appInitializerFactory } from './appInitializerFactory';
import { CartService } from './core/cart.service';
import { CoreModule } from './core/core.module';
import { MyEventService } from './core/event.service';
import { CreatePasswordPage } from './create-password/create-password.page';
import ENV from './env.json';
import { MultiScanResultModalComponent } from './products/multi-scan-result-modal/multi-scan-result-modal.component';
import { ShowBarcodeModalComponent } from './profile/show-barcode-modal/show-barcode-modal.component';
import { SharedModule } from './shared/shared.module';
import { SSOModalComponent } from './login/sso-modal/sso-modal.component';
const createTranslateLoader = (http: HttpClient) => new TranslateHttpLoader(http, 'https://cmh-test.web02.mybusiness.nl/api/translate/', '');
import SwiperCore, { A11y, Autoplay, Controller, FreeMode, Navigation, Pagination, Parallax, Scrollbar, Thumbs, Virtual, Zoom } from 'swiper';
import { SwiperModule } from 'swiper/angular';
import { ZXingScannerModule } from '@zxing/ngx-scanner';

SwiperCore.use([
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Virtual,
  Zoom,
  Autoplay,
  Parallax,
  Thumbs,
  FreeMode,
  Controller
]);



SplashScreen.show();

CapacitorUpdater.addListener('appReady', () => {
  SplashScreen.hide();
})

CapacitorUpdater.notifyAppReady();

registerLocaleData(locales, 'nl-NL', localeNlExtra);

// always return a wrapper object with ngx-resource
ResourceGlobalConfig.returnAs = ResourceActionReturnType.Promise;
ResourceGlobalConfig.queryMappingMethod = ResourceQueryMappingMethod.None;
ResourceGlobalConfig.url = 'https://cmh-test.web02.mybusiness.nl';

Sentry.init({
  enabled: !window.location.origin.startsWith('http://localhost'),
  release: ENV.version + ' ' + ENV.build,
  // enabled: !window.location.origin.startsWith('http://localhost'),
  dsn: 'https://52034100371c5237ad3385d9b08cdd1b@o4506389558329344.ingest.sentry.io/4506439602143232',
  integrations: [
    new Sentry.BrowserTracing({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['localhost', /(http|https):\/\/(.*).mybusiness.nl/],
    }),
    new Sentry.Replay({
      blockAllMedia: false,
      maskAllText: false
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: .5, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: .1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  ignoreErrors: [
    /NG02100/mgi,
    /Http failure response/mgi,
    /"status":423/mgi,
    /"status":400/mgi,
    /"status":401/mgi,
    /"status":404/mgi,
    /"status":0/mgi,
    /'isProxied'/mgi
  ]
});


@NgModule({
  declarations: [AppComponent, CreatePasswordPage, MultiScanResultModalComponent, ShowBarcodeModalComponent, SSOModalComponent],
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    BrowserModule,
    ApiModule,
    FormsModule,
    ZXingScannerModule,
    CoreModule,
    SwiperModule,
    SharedModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ResourceModule.forRoot(),
    IonicModule.forRoot({
      rippleEffect: true,
      mode: 'md'
    }),
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production })
  ],
  providers: [
    MyEventService,
    CartService,
    AuthGuard,
    LoginGuard,
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false, }) },
    { provide: Sentry.TraceService, deps: [Router], },
    { provide: APP_INITIALIZER, useFactory: () => () => { }, deps: [Sentry.TraceService], multi: true },
    { provide: LOCALE_ID, useValue: 'nl-NL' },
    { provide: APP_INITIALIZER, useFactory: appInitializerFactory, deps: [TranslateService, Injector], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
